import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import L from "leaflet";
import { apiGet, apiSend } from "@/lib/api";
import { newBaseMap, projectFor, type BaseProvider } from "@/lib/amap";
import type { Device } from "@/mocks/devices";

// 讲解点采集录制：人在游船上，凭视觉判断"进入/离开某讲解区域"。选定位
// 设备 → 实时看它的位置 → 进入区域点"开始采集"，离开点"结束采集"；
// 期间完整位置流（带时间戳）自动归档，可复盘，并按轨迹质心+离散半径
// 一键生成讲解点（触发圈）。

interface CaptureSession {
  id: string;
  name: string;
  deviceId: string;
  deviceNo: string;
  startedAt: string;
  endedAt: string;
  sceneId: string;
  note: string;
  points: number;
  centerLat: number;
  centerLng: number;
  radiusM: number;
}
interface CapturePoint { at: string; lat: number; lng: number; battery: number }

// 多设备分色：选择顺序决定颜色，地图 marker/轨迹/圈同色。
const COLORS = ["#0d9488", "#f59e0b", "#6366f1", "#ef4444", "#8b5cf6", "#0ea5e9", "#84cc16", "#ec4899"];

function CapturePage() {
  // ── 设备与实时位置（可多选，同时录制）─────────────────────────────
  const [devices, setDevices] = useState<Device[]>([]);
  const [deviceIds, setDeviceIds] = useState<string[]>([]);
  const selected = devices.filter((d) => deviceIds.includes(d.id));
  const colorOf = (id: string) => COLORS[deviceIds.indexOf(id) % COLORS.length];

  const recordingRef = useRef(false);
  const refreshDevices = useCallback(async () => {
    try {
      const ds = (await apiGet<Device[]>("/devices")) ?? [];
      // 采集录制只列两类在线设备：① 途强船载定位器(device_type='tracker'，密集上报，
      // 全船一个事实源)；② 开了采集模式(心跳1s)的手机。眼镜、未开采集模式的手机都不列
      // ——上百台租赁机/眼镜全列出无法选取；它们照常上报心跳/位置，别处(dashboard等)仍可用。
      // 坐标须合法（脏坐标会把地图 fitBounds 拉出墨卡托有效区→瓦片全灰）。
      const usable = ds.filter(
        (d) => d.status === "online" && d.lastLat && d.lastLng &&
          Math.abs(d.lastLat) <= 85 && Math.abs(d.lastLng) <= 180 &&
          (d.deviceType === "tracker" || (d.deviceType === "phone" && d.captureMode)),
      );
      setDevices(usable);
      // 只移除已离线/消失的勾选项；绝不自动补选（否则用户取消后会被重新勾上）。
      // 录制中完全不动选择，避免设备短暂离线打断录制。
      if (!recordingRef.current) {
        setDeviceIds((cur) => cur.filter((id) => usable.some((d) => d.id === id)));
      }
    } catch { /* offline */ }
  }, []);

  useEffect(() => {
    refreshDevices();
    // 2s 轮询：采集模式下设备 1s 上报，端到端延迟 ≈1-3s；页面隐藏时暂停。
    const t = setInterval(() => {
      if (!document.hidden) refreshDevices();
    }, 2000);
    return () => clearInterval(t);
  }, [refreshDevices]);

  // ── 采集会话 ─────────────────────────────────────────────────────
  const [sessions, setSessions] = useState<CaptureSession[]>([]);
  const actives = sessions.filter((s) => !s.endedAt && deviceIds.includes(s.deviceId));
  const recording = actives.length > 0;
  recordingRef.current = recording;
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  // 录制中每个会话的实时轨迹（按 deviceId 存，分色画）
  const [trails, setTrails] = useState<Record<string, CapturePoint[]>>({});

  const reloadSessions = useCallback(async () => {
    try { setSessions((await apiGet<CaptureSession[]>("/captures")) ?? []); } catch { /* offline */ }
  }, []);
  useEffect(() => { reloadSessions(); }, [reloadSessions]);

  // 录制中：计时 + 10s 拉一次各会话明细轨迹
  const activeIdsKey = actives.map((a) => a.id).join(",");
  useEffect(() => {
    if (!recording) { setElapsed(0); setTrails({}); return; }
    const started = Math.min(...actives.map((a) => new Date(a.startedAt.replace(" ", "T")).getTime()));
    const tick = setInterval(() => setElapsed(Math.floor((Date.now() - started) / 1000)), 1000);
    const pull = setInterval(async () => {
      const next: Record<string, CapturePoint[]> = {};
      for (const a of actives) {
        try {
          const d = await apiGet<{ session: CaptureSession; points: CapturePoint[] }>(`/captures/${a.id}`);
          if (d) next[a.deviceId] = d.points;
        } catch { /* offline */ }
      }
      setTrails(next);
    }, 3000);
    return () => { clearInterval(tick); clearInterval(pull); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIdsKey, recording]);

  const startCapture = async () => {
    if (!deviceIds.length) return;
    setBusy(true);
    try {
      // 多设备同名会话：>1 台时名称后缀设备号便于区分
      for (const id of deviceIds) {
        const no = devices.find((d) => d.id === id)?.deviceNo ?? "";
        const n = name.trim();
        await apiSend("POST", "/captures/start", {
          deviceId: id,
          name: deviceIds.length > 1 && n ? `${n}·${no}` : n,
        });
      }
      await reloadSessions();
    } finally { setBusy(false); }
  };
  const stopCapture = async () => {
    if (!recording) return;
    setBusy(true);
    try {
      let last: CaptureSession | null = null;
      for (const a of actives) {
        last = await apiSend<CaptureSession>("POST", `/captures/${a.id}/stop`, {});
      }
      await reloadSessions();
      setName("");
      // 默认录制完就显示在地图上：勾选 + 聚焦最后这条
      if (last?.id) {
        const id = last.id;
        try {
          const d = await apiGet<{ session: CaptureSession; points: CapturePoint[] }>(`/captures/${id}`);
          setShownPoints((cur) => ({ ...cur, [id]: d?.points ?? [] }));
        } catch { /* offline */ }
        setShownIds((cur) => (cur.includes(id) ? cur : [...cur, id]));
        focusIdRef.current = id;
        setFocusTick((t) => t + 1);
      }
    } finally { setBusy(false); }
  };

  // ── 地图：多设备分色 marker（常显设备名）+ 轨迹/圈图层 ───────────
  const mapRef = useRef<L.Map | null>(null);
  const mapDiv = useRef<HTMLDivElement | null>(null);
  const markersRef = useRef<Record<string, L.CircleMarker>>({});
  const layersRef = useRef<L.Layer[]>([]);
  const followedRef = useRef(false);
  // 底图 provider：天地图为主，瓦片连续失败 → 整图回退高德（见 lib/amap newBaseMap）。
  const [provider, setProvider] = useState<BaseProvider>("tdt");
  // 投影：天地图原样 WGS-84；高德需转 GCJ-02。provider 变 → gcj 变 → 各绘制 effect 重投影。
  const gcj = projectFor(provider);
  // 选中显示在地图上的历史记录(可多选,叠加显示;录制完默认勾选)。按选择顺序分色。
  const [shownIds, setShownIds] = useState<string[]>([]);
  const [shownPoints, setShownPoints] = useState<Record<string, CapturePoint[]>>({});
  const shownPointsRef = useRef<Record<string, CapturePoint[]>>({});
  shownPointsRef.current = shownPoints;
  const focusIdRef = useRef<string | null>(null); // 下次绘制把视野 fit 到这条
  const [focusTick, setFocusTick] = useState(0);
  const defaultedRef = useRef(false);       // 首屏默认选中只应用一次
  const fitAllPendingRef = useRef(false);   // 默认选中后,待点加载完 fit 到全部
  const colorOfShown = (id: string) => COLORS[Math.max(0, shownIds.indexOf(id)) % COLORS.length];

  useEffect(() => {
    if (!mapDiv.current || mapRef.current) return;
    const m = newBaseMap(mapDiv.current, provider, () => setProvider("amap"));
    m.setView([30.82, 111.0], 9); // 三峡库区默认视野
    mapRef.current = m;
    return () => {
      m.remove();
      mapRef.current = null;
      // 整图重建（provider 切换）：清掉旧图层/marker 引用，下次按新坐标系从头重绘
      markersRef.current = {};
      layersRef.current = [];
      followedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider]);

  // 实时 marker：每台选中设备一个分色圆点 + 常显设备名标签
  useEffect(() => {
    const m = mapRef.current;
    if (!m) return;
    const markers = markersRef.current;
    // remove deselected
    for (const id of Object.keys(markers)) {
      if (!deviceIds.includes(id)) { markers[id].remove(); delete markers[id]; }
    }
    const pts: [number, number][] = [];
    for (const d of selected) {
      if (!d.lastLat || !d.lastLng) continue;
      const [glat, glng] = gcj(d.lastLat, d.lastLng);
      pts.push([glat, glng]);
      const color = colorOf(d.id);
      if (!markers[d.id]) {
        markers[d.id] = L.circleMarker([glat, glng], {
          radius: 8, color, fillColor: color, fillOpacity: 0.85, weight: 2,
          className: "capture-pulse", // 一闪一闪脉冲,表示该点正在实时上报(active)
        }).addTo(m).bindTooltip(d.deviceNo, {
          permanent: true, direction: "top", offset: [0, -8], className: "capture-dev-label",
        });
      } else {
        // 只有真变化才触碰 Leaflet 图层——2s 轮询下无差别重绘会让点位每次都闪一下
        const cur = markers[d.id].getLatLng();
        if (Math.abs(cur.lat - glat) > 1e-7 || Math.abs(cur.lng - glng) > 1e-7) {
          markers[d.id].setLatLng([glat, glng]);
        }
        if (markers[d.id].options.color !== color) {
          markers[d.id].setStyle({ color, fillColor: color });
        }
      }
    }
    if (!followedRef.current && pts.length) {
      if (pts.length === 1) m.setView(pts[0], 14);
      else m.fitBounds(L.latLngBounds(pts), { padding: [40, 40] });
      followedRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider, deviceIds.join(","), selected.map((d) => `${d.lastLat},${d.lastLng}`).join("|")]);

  // 触发圈 = 轨迹质心 + 最大离散距离（与后端 suggestCircle 同口径）
  const circleOf = (pts: CapturePoint[]): { lat: number; lng: number; r: number } | null => {
    if (!pts.length) return null;
    let lat = 0, lng = 0;
    for (const p of pts) { lat += p.lat; lng += p.lng; }
    lat /= pts.length; lng /= pts.length;
    const R = 6371000;
    let maxD = 0;
    for (const p of pts) {
      const dLat = ((p.lat - lat) * Math.PI) / 180;
      const dLng = ((p.lng - lng) * Math.PI) / 180;
      const a = Math.sin(dLat / 2) ** 2 +
        Math.cos((lat * Math.PI) / 180) * Math.cos((p.lat * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
      maxD = Math.max(maxD, 2 * R * Math.asin(Math.sqrt(a)));
    }
    return { lat, lng, r: Math.ceil(Math.max(100, maxD) / 50) * 50 };
  };

  // 录制中实时轨迹(分色) + 选中的历史记录(多选叠加,分色,带起止端点+触发圈)
  useEffect(() => {
    const m = mapRef.current;
    if (!m) return;
    layersRef.current.forEach((l) => l.remove());
    layersRef.current = [];

    const drawTrail = (pts: CapturePoint[], color: string, withEnds: boolean, sess?: CaptureSession) => {
      if (!pts.length) return;
      if (pts.length > 1) {
        layersRef.current.push(
          L.polyline(pts.map((p) => gcj(p.lat, p.lng)), { color, weight: 3 }).addTo(m),
        );
      }
      for (const pt of pts) {
        layersRef.current.push(
          L.circleMarker(gcj(pt.lat, pt.lng), {
            radius: withEnds ? 3.5 : 3, color, fillColor: color, fillOpacity: 0.8, weight: 1,
          }).addTo(m).bindTooltip(`${pt.at.slice(11)} · ${pt.lat.toFixed(6)},${pt.lng.toFixed(6)}`),
        );
      }
      if (withEnds) {
        // 绿=开始采集位置，红=结束采集位置。标签默认不显示(48 条全显会糊成一片),
        // 鼠标移到端点上才显示「名称 · 开始/结束 时间」。
        const sp = pts[0], ep = pts[pts.length - 1];
        const nm = sess?.name ? `${sess.name} · ` : "";
        layersRef.current.push(
          L.circleMarker(gcj(sp.lat, sp.lng), {
            radius: 7, color: "#059669", fillColor: "#10b981", fillOpacity: 1, weight: 2,
          }).addTo(m).bindTooltip(`${nm}开始 ${sp.at.slice(11)}`, { direction: "bottom", offset: [0, 8] }),
        );
        if (pts.length > 1) {
          layersRef.current.push(
            L.circleMarker(gcj(ep.lat, ep.lng), {
              radius: 7, color: "#dc2626", fillColor: "#ef4444", fillOpacity: 1, weight: 2,
            }).addTo(m).bindTooltip(`${nm}结束 ${ep.at.slice(11)}`, { direction: "top", offset: [0, -8] }),
          );
        }
      }
      const c = sess?.centerLat
        ? { lat: sess.centerLat, lng: sess.centerLng, r: sess.radiusM }
        : circleOf(pts);
      if (c) {
        const [clat, clng] = gcj(c.lat, c.lng);
        layersRef.current.push(
          L.circle([clat, clng], { radius: c.r, color, fillOpacity: 0.08, dashArray: "6 6" })
            .addTo(m).bindTooltip(`触发圈 半径 ${c.r}m`),
        );
      }
    };

    // 录制中：每台设备分色实时轨迹+预览圈
    for (const [devID, pts] of Object.entries(trails)) {
      drawTrail(pts, colorOf(devID), false);
    }
    // 选中的历史记录：分色叠加(带起止端点+触发圈)
    shownIds.forEach((id) => {
      const sess = sessions.find((x) => x.id === id);
      const pts = shownPoints[id];
      if (sess && pts) drawTrail(pts, colorOfShown(id), true, sess);
    });

    // 点"聚焦"：把视野 fit 到该条
    if (focusIdRef.current) {
      const pts = shownPoints[focusIdRef.current];
      focusIdRef.current = null;
      if (pts && pts.length) {
        m.fitBounds(L.latLngBounds(pts.map((p) => gcj(p.lat, p.lng)) as [number, number][]), { padding: [40, 40] });
      }
    }
    // 首屏默认选中"最后一天"后：fit 到全部选中记录的范围。用各记录的触发圈中心
    // (sessions 已带 centerLat/Lng，同步可用)而非异步加载的轨迹点——否则会在第一
    // 条记录的点先加载完时就 fit 到那一条(放得很大)，看不到全部。
    if (fitAllPendingRef.current && shownIds.length) {
      const centers: [number, number][] = [];
      shownIds.forEach((id) => {
        const s = sessions.find((x) => x.id === id);
        if (s && s.centerLat) centers.push(gcj(s.centerLat, s.centerLng));
      });
      if (centers.length) {
        fitAllPendingRef.current = false;
        m.fitBounds(L.latLngBounds(centers), { padding: [50, 50] });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider, trails, shownIds, shownPoints, focusTick]);

  const loadPoints = async (id: string) => {
    if (shownPointsRef.current[id]) return; // 已加载过
    try {
      const d = await apiGet<{ session: CaptureSession; points: CapturePoint[] }>(`/captures/${id}`);
      setShownPoints((cur) => ({ ...cur, [id]: d?.points ?? [] }));
    } catch { /* offline */ }
  };
  const toggleShown = (id: string, on: boolean) => {
    setShownIds((cur) => (on ? (cur.includes(id) ? cur : [...cur, id]) : cur.filter((x) => x !== id)));
    if (on) loadPoints(id);
  };
  const showAll = () => {
    setShownIds(sessions.map((s) => s.id));
    sessions.forEach((s) => loadPoints(s.id));
  };
  const clearShown = () => setShownIds([]);
  const focusSession = (s: CaptureSession) => {
    toggleShown(s.id, true);
    focusIdRef.current = s.id;
    setFocusTick((t) => t + 1);
  };

  // 默认:首屏把"最后一天"的所有采集记录勾选、显示并 fit 到地图。
  // 注意"最后一天"=采集数据里最近的那个日期(取最大 startedAt 的日期,数据驱动),
  // 不是 new Date() 的今天——今天没采集时会自动落到之前最近有采集的那天。
  // 只应用一次(defaultedRef),之后用户的勾选/清除不被覆盖。
  useEffect(() => {
    if (defaultedRef.current || sessions.length === 0) return;
    defaultedRef.current = true;
    const lastDay = sessions.map((s) => s.startedAt.slice(0, 10)).sort().at(-1);
    const ids = sessions.filter((s) => s.startedAt.slice(0, 10) === lastDay).map((s) => s.id);
    setShownIds(ids);
    ids.forEach((id) => loadPoints(id));
    fitAllPendingRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessions]);

  // 行内明细展开：每条记录都能点开看自己的完整位置明细（时间戳/坐标/电量）
  const [detailId, setDetailId] = useState("");
  const [detailPoints, setDetailPoints] = useState<CapturePoint[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const toggleDetail = async (s: CaptureSession) => {
    if (detailId === s.id) { setDetailId(""); setDetailPoints([]); return; }
    setDetailId(s.id);
    setDetailPoints([]);
    setDetailLoading(true);
    try {
      const d = await apiGet<{ session: CaptureSession; points: CapturePoint[] }>(`/captures/${s.id}`);
      setDetailPoints(d?.points ?? []);
    } catch { setDetailPoints([]); }
    finally { setDetailLoading(false); }
  };

  // ── 历史操作 ─────────────────────────────────────────────────────
  const [editingId, setEditingId] = useState("");
  const [editName, setEditName] = useState("");
  const rename = async (s: CaptureSession) => {
    if (editName.trim() && editName.trim() !== s.name) {
      await apiSend("PUT", `/captures/${s.id}`, { name: editName.trim(), note: s.note });
      await reloadSessions();
    }
    setEditingId("");
  };
  const makeScene = async (s: CaptureSession) => {
    if (!window.confirm(`用「${s.name}」的轨迹生成讲解点？\n中心 ${s.centerLat.toFixed(5)}, ${s.centerLng.toFixed(5)} · 半径 ${s.radiusM}m\n（生成后状态为"未激活"，请在点位管理完善讲解词后激活）`)) return;
    await apiSend("POST", `/captures/${s.id}/scene`, {});
    await reloadSessions();
  };
  const remove = async (s: CaptureSession) => {
    if (!window.confirm(`删除采集「${s.name}」？（不影响已生成的讲解点）`)) return;
    await apiSend("DELETE", `/captures/${s.id}`);
    setShownIds((cur) => cur.filter((x) => x !== s.id));
    setShownPoints((cur) => { const n = { ...cur }; delete n[s.id]; return n; });
    if (detailId === s.id) { setDetailId(""); setDetailPoints([]); }
    await reloadSessions();
  };

  const fmtDur = (sec: number) =>
    sec >= 3600 ? `${Math.floor(sec / 3600)}h${Math.floor((sec % 3600) / 60)}m` : `${Math.floor(sec / 60)}m${sec % 60}s`;

  const durationOf = (s: CaptureSession) => {
    if (!s.endedAt) return "录制中";
    const a = new Date(s.startedAt.replace(" ", "T")).getTime();
    const b = new Date(s.endedAt.replace(" ", "T")).getTime();
    return fmtDur(Math.max(0, Math.floor((b - a) / 1000)));
  };

  const totalTrailPoints = useMemo(
    () => Object.values(trails).reduce((n, p) => n + p.length, 0),
    [trails],
  );

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-semibold text-gray-900">讲解点采集录制</h1>
        <p className="text-sm text-gray-500 mt-1">
          在船上凭视觉判断讲解区域：进入时开始采集、离开时结束；期间位置流自动归档，可复盘并一键生成讲解点
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* 控制面板 */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1.5">
              定位设备 <span className="text-gray-400">（仅在线可采集，可多选同时录制）</span>
            </label>
            <div className="space-y-1.5 max-h-44 overflow-y-auto pr-1">
              {devices.length === 0 && (
                <div className="text-sm text-gray-400 py-2">（暂无有定位上报的设备）</div>
              )}
              {devices.map((d) => {
                const checked = deviceIds.includes(d.id);
                return (
                  <label
                    key={d.id}
                    className={`flex items-center gap-2.5 px-3 py-2 rounded-lg border cursor-pointer text-sm ${
                      checked ? "border-teal-300 bg-teal-50/50" : "border-gray-200 hover:bg-gray-50"
                    } ${recording ? "opacity-60 pointer-events-none" : ""}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => {
                        setDeviceIds((cur) =>
                          e.target.checked ? [...cur, d.id] : cur.filter((x) => x !== d.id),
                        );
                        followedRef.current = false;
                      }}
                      className="accent-teal-600"
                    />
                    {checked && (
                      <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: colorOf(d.id) }} />
                    )}
                    <span className="font-mono text-xs">{d.deviceNo}</span>
                    {d.deviceType === ("tracker" as Device["deviceType"]) && (
                      <span className="px-1.5 py-0.5 rounded bg-sky-100 text-sky-700 text-[10px]">船载</span>
                    )}
                    <span className={`ml-auto text-[11px] ${d.status === "online" ? "text-emerald-600" : "text-gray-400"}`}>
                      {d.status === "online" ? "在线" : "离线"}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-3 space-y-1.5">
            <div className="text-xs text-gray-500 mb-1">实时位置（WGS-84，2s 刷新）</div>
            {selected.length === 0 && <div className="text-sm text-gray-400">未选择设备</div>}
            {selected.map((d) => (
              <div key={d.id} className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: colorOf(d.id) }} />
                <span className="font-mono text-xs text-gray-900">
                  {d.lastLat?.toFixed(6)}, {d.lastLng?.toFixed(6)}
                </span>
                <span className="ml-auto text-[10px] text-gray-400" title="位置实际采集时间（非心跳时间）">
                  定位于 {d.lastPosAt || "未知"}
                </span>
              </div>
            ))}
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1.5">讲解点名称（可此刻填，也可事后改）</label>
            <input
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
              placeholder="如：屈原故里讲解区"
              value={recording ? actives[0]?.name ?? "" : name}
              onChange={(e) => setName(e.target.value)}
              disabled={recording}
            />
          </div>

          {!recording ? (
            <button
              onClick={startCapture}
              disabled={!deviceIds.length || busy}
              className="w-full py-3 rounded-xl bg-teal-600 text-white font-semibold text-sm hover:bg-teal-700 disabled:opacity-40 flex items-center justify-center gap-2"
            >
              <i className="ri-record-circle-line text-lg" />
              开始采集（已进入讲解区域{deviceIds.length > 1 ? ` · ${deviceIds.length}台` : ""}）
            </button>
          ) : (
            <div className="space-y-3">
              <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2 text-sm font-medium text-red-700">
                    <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" />
                    录制中 {fmtDur(elapsed)}
                  </span>
                  <span className="text-xs text-red-500">{totalTrailPoints} 个位置点</span>
                </div>
                {actives.length > 1 && actives.map((a) => (
                  <div key={a.id} className="flex items-center gap-2 text-[11px] text-red-600/80">
                    <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: colorOf(a.deviceId) }} />
                    {a.deviceNo} · {(trails[a.deviceId] ?? []).length} 点
                  </div>
                ))}
              </div>
              <button
                onClick={stopCapture}
                disabled={busy}
                className="w-full py-3 rounded-xl bg-red-600 text-white font-semibold text-sm hover:bg-red-700 disabled:opacity-40 flex items-center justify-center gap-2"
              >
                <i className="ri-stop-circle-line text-lg" />
                结束采集（已离开讲解区域）
              </button>
            </div>
          )}

          <p className="text-xs text-gray-400 leading-relaxed">
            提示：位置来自设备上报流（采集模式手机约 1s/次、途强船载定位器约 10s/次），
            录制窗口内的所有点都会归档；结束后按轨迹自动给出建议触发圈。
          </p>
        </div>

        {/* 地图 */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-4 py-2.5 border-b border-gray-100 flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">
              {recording ? "实时轨迹（录制中）" : shownIds.length ? `地图显示 ${shownIds.length} 条采集` : "设备实时位置"}
            </span>
            {shownIds.length > 0 && (
              <button
                onClick={clearShown}
                className="text-xs text-teal-600 hover:text-teal-700"
              >
                <i className="ri-eye-off-line mr-1" />清除显示
              </button>
            )}
          </div>
          <div ref={mapDiv} className="h-[300px] sm:h-[420px]" />
        </div>
      </div>

      {/* 历史采集 */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-800">采集记录</h2>
          <div className="flex items-center gap-3 text-xs">
            <button onClick={showAll} className="text-teal-600 hover:text-teal-700">
              <i className="ri-checkbox-multiple-line mr-0.5" />全部显示
            </button>
            <button onClick={clearShown} className="text-gray-400 hover:text-gray-600">清除</button>
            <span className="text-gray-400">{sessions.length} 条</span>
          </div>
        </div>
        <div className="overflow-auto max-h-[480px]">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="sticky top-0 bg-white z-10">
            <tr className="text-left text-xs text-gray-400 border-b border-gray-100">
              <th className="px-3 py-2.5 font-medium w-9 text-center">
                <input
                  type="checkbox"
                  checked={sessions.length > 0 && shownIds.length === sessions.length}
                  ref={(el) => { if (el) el.indeterminate = shownIds.length > 0 && shownIds.length < sessions.length; }}
                  onChange={(e) => (e.target.checked ? showAll() : clearShown())}
                  className="accent-teal-600"
                  title="全选 / 清除"
                />
              </th>
              <th className="px-5 py-2.5 font-medium">名称</th>
              <th className="px-3 py-2.5 font-medium">设备</th>
              <th className="px-3 py-2.5 font-medium">开始 / 结束</th>
              <th className="px-3 py-2.5 font-medium">时长</th>
              <th className="px-3 py-2.5 font-medium">点数</th>
              <th className="px-3 py-2.5 font-medium">建议触发圈</th>
              <th className="px-3 py-2.5 font-medium text-right pr-5">操作</th>
            </tr>
          </thead>
          <tbody>
            {sessions.map((s) => (
              <Fragment key={s.id}>
              <tr className={`border-b border-gray-50 hover:bg-gray-50/60 ${!s.endedAt ? "bg-red-50/40" : shownIds.includes(s.id) ? "bg-teal-50/30" : ""} ${detailId === s.id ? "bg-teal-50/40" : ""}`}>
                <td className="px-3 py-3 text-center">
                  <input
                    type="checkbox"
                    checked={shownIds.includes(s.id)}
                    onChange={(e) => toggleShown(s.id, e.target.checked)}
                    className="accent-teal-600"
                    title="在地图上显示这条采集"
                  />
                </td>
                <td className="px-5 py-3">
                  {editingId === s.id ? (
                    <input
                      autoFocus
                      className="px-2 py-1 border border-teal-300 rounded text-sm w-44"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      onBlur={() => rename(s)}
                      onKeyDown={(e) => e.key === "Enter" && rename(s)}
                    />
                  ) : (
                    <button
                      className="font-medium text-gray-900 hover:text-teal-700 inline-flex items-center gap-1.5"
                      onClick={() => { setEditingId(s.id); setEditName(s.name); }}
                      title="点击重命名"
                    >
                      {s.name}
                      <i className="ri-pencil-line text-gray-300 text-xs" />
                    </button>
                  )}
                  {s.sceneId && (
                    <span className="ml-2 px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 text-[10px]">已生成讲解点</span>
                  )}
                </td>
                <td className="px-3 py-3 font-mono text-xs text-gray-600">{s.deviceNo}</td>
                <td className="px-3 py-3 text-xs text-gray-600">
                  {s.startedAt}<br />{s.endedAt || <span className="text-red-500 font-medium">录制中…</span>}
                </td>
                <td className="px-3 py-3 text-gray-700">{durationOf(s)}</td>
                <td className="px-3 py-3 text-gray-700">{s.points}</td>
                <td className="px-3 py-3 text-xs font-mono text-gray-600">
                  {s.points > 0 ? `${s.centerLat.toFixed(5)}, ${s.centerLng.toFixed(5)} · ${s.radiusM}m` : "—"}
                </td>
                <td className="px-3 py-3 text-right pr-5 whitespace-nowrap">
                  <button
                    onClick={() => toggleDetail(s)}
                    className={`text-xs mr-3 ${detailId === s.id ? "text-teal-700 font-medium" : "text-gray-500 hover:text-teal-700"}`}
                  >
                    <i className={`mr-0.5 ${detailId === s.id ? "ri-arrow-up-s-line" : "ri-list-check"}`} />明细
                  </button>
                  <button onClick={() => focusSession(s)} className="text-teal-600 hover:text-teal-700 text-xs mr-3">
                    <i className="ri-focus-3-line mr-0.5" />聚焦
                  </button>
                  {s.endedAt && !s.sceneId && s.points > 0 && (
                    <button onClick={() => makeScene(s)} className="text-indigo-600 hover:text-indigo-700 text-xs mr-3">
                      <i className="ri-map-pin-add-line mr-0.5" />生成讲解点
                    </button>
                  )}
                  <button onClick={() => remove(s)} className="text-gray-400 hover:text-red-600 text-xs">
                    <i className="ri-delete-bin-line mr-0.5" />删除
                  </button>
                </td>
              </tr>

              {/* 行内位置明细：每条记录独立展开 */}
              {detailId === s.id && (
                <tr className="bg-gray-50/60">
                  <td colSpan={8} className="px-0 py-0">
                    <div className="px-5 py-2 text-xs text-gray-500 flex items-center justify-between">
                      <span>「{s.name}」位置明细 · 完整信号数据（WGS-84）</span>
                      <span>{detailLoading ? "加载中…" : `${detailPoints.length} 点`}</span>
                    </div>
                    {!detailLoading && detailPoints.length > 0 && (
                      <div className="max-h-72 overflow-y-auto border-t border-gray-100">
                        <table className="w-full text-xs font-mono">
                          <thead className="sticky top-0 bg-gray-100 text-gray-500">
                            <tr>
                              <th className="px-5 py-1.5 text-left font-medium w-10">#</th>
                              <th className="px-3 py-1.5 text-left font-medium">时间戳</th>
                              <th className="px-3 py-1.5 text-left font-medium">纬度 (lat)</th>
                              <th className="px-3 py-1.5 text-left font-medium">经度 (lng)</th>
                              <th className="px-3 py-1.5 text-left font-medium">电量</th>
                            </tr>
                          </thead>
                          <tbody>
                            {detailPoints.map((p, i) => (
                              <tr key={i} className="border-b border-gray-100/70 hover:bg-white">
                                <td className="px-5 py-1.5 text-gray-300">{i + 1}</td>
                                <td className="px-3 py-1.5 text-gray-500">{p.at}</td>
                                <td className="px-3 py-1.5 text-gray-800">{p.lat.toFixed(6)}</td>
                                <td className="px-3 py-1.5 text-gray-800">{p.lng.toFixed(6)}</td>
                                <td className="px-3 py-1.5 text-gray-400">{p.battery >= 0 ? `${p.battery}%` : "—"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                    {!detailLoading && detailPoints.length === 0 && (
                      <div className="px-5 py-6 text-center text-xs text-gray-400 border-t border-gray-100">
                        该采集窗口内没有位置上报记录（设备当时未获得有效定位）
                      </div>
                    )}
                  </td>
                </tr>
              )}
              </Fragment>
            ))}
            {sessions.length === 0 && (
              <tr>
                <td colSpan={8} className="px-5 py-10 text-center text-sm text-gray-400">
                  还没有采集记录——选择设备后点击"开始采集"
                </td>
              </tr>
            )}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}

export default CapturePage;
